import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  signal,
  computed,
  inject,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { PlayersService } from '../../api-client/api/players.service';
import { ModulesPlayersApplicationPlayersPlayerResponse } from '../../api-client/model/modulesPlayersApplicationPlayersPlayerResponse';
import { SortableHeaderComponent } from '../shared/sortable-header/sortable-header.component';
import { SortConfig, TableSortingService } from '../../services';

@Component({
  selector: 'app-player-lookup-modal',
  standalone: true,
  imports: [FormsModule, SortableHeaderComponent],
  providers: [PlayersService],
  templateUrl: './player-lookup-modal.component.html',
  styleUrls: ['./player-lookup-modal.component.scss'],
})
export class PlayerLookupModalComponent {
  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;

  private playersService = inject(PlayersService);
  private tableSortingService = inject(TableSortingService);

  // Signals for reactive state management
  private allPlayers = signal<ModulesPlayersApplicationPlayersPlayerResponse[]>([]);
  sortConfig = signal<SortConfig | null>(null);
  searchTerm = signal<string>('');
  isLoading = signal<boolean>(false);
  isVisible = signal<boolean>(false);
  excludedPlayerIds = signal<string[]>([]);

  // Computed filtered and sorted players
  filteredPlayers = computed(() => {
    const players = this.allPlayers();
    const searchTerm = this.searchTerm().toLowerCase();
    const sort = this.sortConfig();
    const excluded = this.excludedPlayerIds();

    // First, filter out players already in the tournament
    let filtered = excluded.length > 0
      ? players.filter(player => !excluded.includes(player.playerId || ''))
      : players;

    // Filter by search term (searches name, LPGA ID, WWGR ID)
    filtered = searchTerm
      ? filtered.filter(
          (player) =>
            player.firstName?.toLowerCase().includes(searchTerm) ||
            player.lastName?.toLowerCase().includes(searchTerm) ||
            player.shortName?.toLowerCase().includes(searchTerm) ||
            player.lpgaId?.toString().includes(searchTerm) ||
            player.wwgrId?.toString().includes(searchTerm)
        )
      : filtered;

    // Apply sorting if configured
    if (sort) {
      filtered = this.tableSortingService.sort(filtered, sort.key, sort.direction);
    }

    return filtered;
  });

  @Output() playerSelected = new EventEmitter<ModulesPlayersApplicationPlayersPlayerResponse>();

  @HostListener('document:keydown.escape')
  onKeydownHandler() {
    if (this.isVisible()) {
      this.closeModal();
    }
  }

  openModal(excludedPlayerIds: string[] = []): void {
    this.excludedPlayerIds.set(excludedPlayerIds);
    this.isVisible.set(true);
    this.searchTerm.set('');
    this.loadPlayers();

    // Focus search input after modal opens
    requestAnimationFrame(() => {
      this.searchInput?.nativeElement?.focus();
    });
  }

  closeModal(): void {
    this.isVisible.set(false);
  }

  private loadPlayers(): void {
    this.isLoading.set(true);
    this.playersService.getPlayersEndpoint().subscribe({
      next: (players) => {
        this.allPlayers.set(players);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading players:', error);
        this.isLoading.set(false);
      },
    });
  }

  selectPlayer(player: ModulesPlayersApplicationPlayersPlayerResponse): void {
    this.playerSelected.emit(player);
    this.closeModal();
  }

  onSort(event: { key: string; direction: 'asc' | 'desc' }) {
    this.sortConfig.set({ key: event.key, direction: event.direction });
  }

  trackByPlayerId(index: number, player: ModulesPlayersApplicationPlayersPlayerResponse): string {
    return player.playerId || index.toString();
  }
}
